import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, TextInput } from '@kontentino/ui';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import useCopyToClipboard from 'app/hooks/useCopyToClipboard';

interface InviteLinkShareProps {
  link: string;
}

const COPIED_FEEDBACK_MS = 2500;

const InviteLinkShare: FC<InviteLinkShareProps> = ({ link }) => {
  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleCopy = useCallback(() => {
    copyToClipboard(link, { message: t('inviteUserModal.linkCopied') });
    setIsCopied(true);
    clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(
      () => setIsCopied(false),
      COPIED_FEEDBACK_MS,
    );
  }, [copyToClipboard, link, t]);

  useEffect(() => () => clearTimeout(copyTimeoutRef.current), []);

  return (
    <div className="tw-mb-6 tw-rounded-lg tw-border tw-border-grayscale-30 tw-p-4">
      <div className="tw-mb-1 tw-flex tw-items-center tw-gap-2">
        <Icon icon={faLink} className="tw-text-grayscale-100" />
        <span className="tw-font-medium tw-text-grayscale-180">
          {t('inviteUserModal.shareLinkTitle')}
        </span>
      </div>
      <p className="tw-mb-3 tw-text-sm tw-text-grayscale-100">
        {t('inviteUserModal.shareLinkDescription')}
      </p>
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-flex-1">
          <TextInput
            disabled
            value={link}
            className="tw-w-full"
            data-cy="invite-user-modal_share-link-input"
          />
        </div>
        <Button
          variant={isCopied ? 'success' : 'secondary'}
          className="tw-shrink-0"
          iconBefore={<Icon icon={isCopied ? faCheck : faCopy} />}
          onClick={handleCopy}
          data-cy="invite-user-modal_share-link-copy-button"
        >
          {isCopied ? t('inviteUserModal.linkCopiedShort') : t('copy')}
        </Button>
      </div>
    </div>
  );
};

export default InviteLinkShare;
